import AlertService from "../../../../services/AlertService";
import PracticalService from "../../../../services/PracticalService";
import AuthContext from "../../../../auth/AuthContext";
import { Fragment, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import PracticalQuestion from "./PracticalQuestion";
import { Button } from "react-bootstrap";
import { Skeleton } from "primereact/skeleton";

const AssessmentPractical = () => {
    const navigate = useNavigate();
    const { assessmentName } = useParams();
    const { state } = useLocation();
    const [loading, setLoading] = useState(false);
    const [questions, setQuestions] = useState([]);
    const [deletedIds, setDeletedIds] = useState([]);
    const [permissions, setPermissions] = useState({});
    const authContext = useContext(AuthContext);
    const submitControllerRef = useRef(null);

    const generateUniqueId = useCallback(() => 'New_' + Date.now().toString(36) + Math.random().toString(36).substr(2), []);

    useEffect(() => {
        const assessmentAccess = authContext.getPermissions("assessmentsmaster");
        if (assessmentAccess.filter((f) => f.access.includes(1)).length === 0) {
            navigate("/");
        }
        setPermissions({
            showAdd: assessmentAccess.some(f => f.access.includes(2)),
            showEdit: assessmentAccess.some(f => f.access.includes(3)),
            showDelete: assessmentAccess.some(f => f.access.includes(4)),
        });
    }, [authContext, navigate]);

    const handleAddQuestion = useCallback(async () => {
        let newQ = {
            id: generateUniqueId(),
            assessmentId: state?.id,
            orderBy: questions.length + 1,
            storyBoardTitle: "",
            storyBoardInfo: "",
            taskTitle: "",
            taskInfo: "",
        };
        setQuestions((prev) => {
            return [...prev, newQ];
        });
    }, [state?.id, generateUniqueId, questions.length]);

const loadQuestions = useCallback(async (signal) => { 
    setLoading(true);
    try {
      const data = await PracticalService.GetAll(
        state?.id,
        authContext,
        signal 
      );

      if (signal?.aborted) return; 

      if (data.isSuccess) {
        setQuestions(data.data);
      } else {
        AlertService.error(data.errorMessage);
      }
    } catch (err) {
      if (err.name === "AbortError") return; 
    } finally {
      if (!signal?.aborted) setLoading(false); 
    }
  }, [authContext, state?.id]);

    useEffect(() => {
        if (questions.length === 0) {
            handleAddQuestion();
        }
    }, [questions, handleAddQuestion]);

useEffect(() => {
    const controller = new AbortController(); 
    if (state?.id) {
      loadQuestions(controller.signal);
    }
    return () => controller.abort(); 
  }, [loadQuestions, authContext, state?.id]);

    const handleStoryBoardTitleChange = useCallback((questionId, storyBoardTitle) => {
        setQuestions(prevQuestions =>
            prevQuestions.map(q =>
                q.id === questionId ? { ...q, storyBoardTitle: storyBoardTitle } : q
            )
        );
    }, []);

    const handleStoryBoardInfoChange = useCallback((questionId, storyBoardInfo) => {
        setQuestions(prevQuestions =>
            prevQuestions.map(q =>
                q.id === questionId ? { ...q, storyBoardInfo: storyBoardInfo } : q
            )
        );
    }, []);
    const handleTaskTitleChange = useCallback((questionId, taskTitle) => {
        setQuestions(prevQuestions =>
            prevQuestions.map(q =>
                q.id === questionId ? { ...q, taskTitle: taskTitle } : q
            )
        );
    }, []);

    const handleTaskInfoChange = useCallback((questionId, taskInfo) => {
        setQuestions(prevQuestions =>
            prevQuestions.map(q =>
                q.id === questionId ? { ...q, taskInfo: taskInfo } : q
            )
        );
    }, []);

    const removeHandler = useCallback((qIdToDelete) => {
        setQuestions((prevQuestions) => {
            const filteredQuestions = prevQuestions.filter(f => f.id !== qIdToDelete);
            // Re-calculate orderBy for remaining questions
            const updatedQuestions = filteredQuestions.map((q, index) => ({
                ...q,
                orderBy: index + 1
            }));
            return updatedQuestions;
        });

        // Add to deletedIds if it's an existing question (not a 'New_' temp ID)
        if (!qIdToDelete.toString().startsWith('New_')) {
            setDeletedIds((prev) => [...prev, qIdToDelete]);
        }
    }, []);

    const prepareQuestionsPayload = useCallback(() => {
        return questions.map(q => ({
            id: q.id.toString().startsWith('New_') ? 0 : q.id,
            assessmentId: q.assessmentId,
            orderBy: q.orderBy,
            storyBoardTitle: q.storyBoardTitle,
            storyBoardInfo: q.storyBoardInfo,
            taskTitle: q.taskTitle,
            taskInfo: q.taskInfo,
        }));
    }, [questions]);

    const onCancelHandler = useCallback(() => {
        navigate(`/masters-assessments/${assessmentName}`, { state: { id: state?.id } });
    }, [assessmentName, navigate, state?.id]);



const onSubmitHandler = useCallback(async (event) => {
  event.preventDefault();

  if (questions.length === 0) return;

  if (submitControllerRef.current) {
    submitControllerRef.current.abort();
  }
  const controller = new AbortController();
  submitControllerRef.current = controller;

  setLoading(true);

  try {
    const payload = {
      ToBeDeleted: deletedIds,
      models: prepareQuestionsPayload(),
    };

    const data = await PracticalService.BulkSave(
      payload,
      authContext,
      controller.signal
    );

    if (controller.signal.aborted) return;

    if (data.isSuccess) {
      AlertService.success("Questions saved successfully!");
      onCancelHandler();
    } else {
      AlertService.error(data.errorMessage);
    }
  } catch (ex) {
    if (ex.name === "AbortError") return;
    console.log(ex);
  } finally {
    if (!controller.signal.aborted) setLoading(false);
  }
}, [questions, deletedIds, authContext, onCancelHandler, prepareQuestionsPayload]);

// Unmount cleanup
useEffect(() => {
  return () => {
    submitControllerRef.current?.abort();
  };
}, []);

    return (
        <Fragment>
            <div className="container-fluid custom-container mt-3">
                {loading ? ((
                    Array.from({ length: 3 }).map((_, idx) => (
                        <div key={idx} className="col-12">
                            <div key={idx} className="border-0 h-100"
                                style={{ cursor: "pointer", minHeight: "100px" }}
                            >
                                <center className="d-flex justify-content-start fw-bold w-100 mb-3">
                                    <Skeleton width="15%" height="2rem" />
                                </center>
                                <center className="d-flex justify-content-start fw-bold w-100 mb-3">
                                    <Skeleton width="100%" height="2px" />
                                </center>
                                <center className="d-flex justify-content-start fw-bold w-100 mb-3 ps-3">
                                    <Skeleton width="15%" height="2rem" />
                                </center>
                                <center className="d-flex justify-content-start fw-bold w-100 mb-3 ps-3">
                                    <Skeleton width="100%" height="2rem" />
                                </center>
                                <center className="d-flex justify-content-start fw-bold w-100 mb-3 ps-3">
                                    <Skeleton width="15%" height="2rem" />
                                </center>
                                <center className="d-flex justify-content-start fw-bold w-100 mb-3 ps-3">
                                    <Skeleton width="100%" height="10rem" />
                                </center>
                                <center className="d-flex justify-content-start fw-bold w-100 mb-3 ps-3">
                                    <Skeleton width="15%" height="2rem" />
                                </center>
                                <center className="d-flex justify-content-start fw-bold w-100 mb-3 ps-3">
                                    <Skeleton width="100%" height="2rem" />
                                </center>
                                <center className="d-flex justify-content-start fw-bold w-100 mb-3 ps-3">
                                    <Skeleton width="15%" height="2rem" />
                                </center>
                                <center className="d-flex justify-content-start fw-bold w-100 mb-3 ps-3">
                                    <Skeleton width="100%" height="2rem" />
                                </center>
                                <center className="d-flex justify-content-start fw-bold w-100 mb-3">
                                    <Skeleton width="100%" height="2px" />
                                </center>
                            </div>
                        </div>
                    )))) :
                    (questions &&
                        questions.length > 0 &&
                        questions.map((m, i) => (
                            <PracticalQuestion
                                key={m.id}
                                index={i}
                                input={m}
                                onStoryBoardTitleChange={handleStoryBoardTitleChange}
                                onStoryBoardInfoChange={handleStoryBoardInfoChange}
                                onTaskTitleChange={handleTaskTitleChange}
                                onTaskInfoChange={handleTaskInfoChange}
                                removeHandler={removeHandler}
                            ></PracticalQuestion>
                        )))}
                <div className="d-flex bd-highlight mb-3 py-2 px-3">
                    <Button
                        variant="primary"
                        className="me-auto px-4 px-md-5 py-2 me-4"
                        onClick={handleAddQuestion}
                        disabled={loading}
                    >
                        Add Question
                    </Button>
                    <button

                        className="btn btn-outline px-4 px-md-5 py-2 me-4"
                        onClick={onCancelHandler}
                        disabled={loading}
                    >
                        Cancel
                    </button>
                    {((permissions.showAdd && Number(state?.id) === 0) || (permissions.showEdit && Number(state?.id) !== 0)) && questions && questions.length > 0 && <Button
                        variant="primary"
                        className="px-4 px-md-5 py-2"
                        onClick={onSubmitHandler}
                        disabled={loading}
                    >Submit</Button>}
                </div>
            </div>
        </Fragment>
    );
};

export default AssessmentPractical;
